import { useMemo, useState, useEffect } from 'react';
import moment from 'moment';
import { useForm, FormProvider } from 'react-hook-form-v7';
import { DATE_FORMATS } from '../../../../utils/Application_Constants';
import useGenerateInvoices from '../../../hooks/useGenerateInvoices';
import { useInvoicingSelectionContext } from '../../../Contexts/InvoicingSelectionContext';
import formatDate from '../../../../utils/formatDate';
import InvoiceConfirmationModal from '../InvoiceConfirmationModal';

const InvoiceInitiationConfirmationModal = ({
    open,
    onClose,
    action,
    doNotSendToClient,
    setDoNotSendToClient,
    setSuccessState,
    refetch,
    invoiceThroughDate,
}) => {
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const initializeFormData = () => ({
        postingDate: moment(Date.now()),
        invoiceDate: moment(Date.now()),
        doNotSendToClient: doNotSendToClient ?? false,
    });
    const [submissionError, setSubmissionError] = useState({ message: '', errors: [] });
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    const { selectedItemsCount, getSelectedItems, deselectAllItems } =
        useInvoicingSelectionContext();

    const formMethods = useForm({ defaultValues: initializeFormData(), mode: 'onBlur' });
    const watchDoNotSendToClient = formMethods.watch('doNotSendToClient');

    useEffect(() => {
        if (watchDoNotSendToClient !== doNotSendToClient) {
            setDoNotSendToClient(watchDoNotSendToClient);
        }
    }, [watchDoNotSendToClient, doNotSendToClient, setDoNotSendToClient]);

    const handleClose = () => {
        setIsProcessing(false);
        setProgress({ current: 0, total: 0 });
        if (hasSubmitted) {
            deselectAllItems();
            refetch();
            setHasSubmitted(false);
        }
        setSubmissionError({ message: '', errors: [] });
        formMethods.reset(initializeFormData());
        onClose();
    };

    const { mutateAsync: generateInvoices } = useGenerateInvoices();

    // Each chunk is all selected items for a given clientId
    const clientInvoicesMap = useMemo(() => {
        const clientIdGroups = new Map();
        getSelectedItems()?.forEach((item) => {
            const clientId = item?.clientId ?? null;
            const existing = clientIdGroups.get(clientId) ?? [];
            existing.push(item);
            clientIdGroups.set(clientId, existing);
        });
        return clientIdGroups;
    }, [getSelectedItems]);

    const onSubmit = async (formData) => {
        setHasSubmitted(true);
        setIsProcessing(true);
        setSuccessState({ showAlert: false, count: 0, action: '' });
        setSubmissionError({ message: '', errors: [] });

        const dataToSubmit = {
            ...formData,
            invoiceDate: formatDate(formData.invoiceDate.toDate(), {
                format: DATE_FORMATS.ISO_8601,
            }),
            postingDate: formatDate(formData.postingDate.toDate(), {
                format: DATE_FORMATS.ISO_8601,
            }),
            toInvoice: [],
            preInvoice: action === 'pre-invoice',
        };

        setProgress({ current: 0, total: clientInvoicesMap.size });

        let successCount = 0;
        let hasErrors = false;
        let errors = [];
        await clientInvoicesMap.values().reduce(async (prev, clientChunk, i) => {
            await prev;
            try {
                await generateInvoices({
                    ...dataToSubmit,
                    toInvoice: clientChunk,
                    totalBookingCount: getSelectedItems()?.length ?? 0,
                });
                successCount += clientChunk.length;
            } catch (error) {
                hasErrors = true;
                if (error?.errorId === 400 || Array.isArray(error?.errors)) {
                    errors = [...errors, ...error.errors];
                } else {
                    const failedBookings = clientChunk.map(
                        ({ bookingId }) => `BKG-${bookingId} failed to process.`
                    );
                    if (failedBookings) {
                        errors = [...errors, ...failedBookings];
                    }
                }
            } finally {
                setProgress({ current: i + 1, total: clientInvoicesMap.size });
            }
        }, Promise.resolve());

        setIsProcessing(false);
        setProgress({ current: 0, total: clientInvoicesMap.size });

        if (hasErrors) {
            setSubmissionError({
                message: `Finished processing with errors. ${successCount} of ${selectedItemsCount} item(s) were ${action}d.`,
                errors: errors.length > 0 ? errors : [],
            });
        } else {
            setSuccessState({ showAlert: true, count: selectedItemsCount, action });
            deselectAllItems();
            refetch();
            onClose();
        }
    };

    return (
        <FormProvider {...formMethods}>
            <InvoiceConfirmationModal
                open={open}
                onClose={handleClose}
                title={`Confirm ${action === 'pre-invoice' ? 'Pre-' : ''}Invoices`}
                bodyMessage={`You've selected ${selectedItemsCount} unbilled item(s) for ${clientInvoicesMap.size} client(s). Do you wish to process them?`}
                showInvoiceForm
                showDoNotSendToClient={action !== 'pre-invoice'}
                onSubmit={formMethods.handleSubmit(onSubmit)}
                error={submissionError}
                isLoading={isProcessing}
                progress={progress}
                actionButtonLabel={`Create ${action === 'pre-invoice' ? 'Pre-' : ''}Invoices`}
                invoiceThroughDate={invoiceThroughDate}
            />
        </FormProvider>
    );
};

export default InvoiceInitiationConfirmationModal;
